import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import SEOHead from '@/components/base/SEOHead';
import { movies, categories, heroSlides } from '@/mocks/movies';

export default function Home() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 600);
    },
    [isTransitioning],
  );

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  }, [currentSlide, heroSlides.length, goToSlide]);

  useEffect(() => {
    autoPlayRef.current = setInterval(nextSlide, 5000);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [nextSlide]);

  const hotMovies = movies.filter((m) => m.isHot).slice(0, 8);
  const newMovies = movies.filter((m) => m.isNew).slice(0, 7);
  const allMovies = movies;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蘑菇影视',
    url: 'https://mogutv.com',
    description: '蘑菇影视官网首页入口，为您提供电影、电视剧、综艺、动漫等高清免费在线观看服务，播放流畅不缓冲。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-0000',
      contactType: 'customer service',
      email: 'contact@mogutv.com',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '蘑菇影视',
    url: 'https://mogutv.com',
    description: '蘑菇影视官网首页入口，为您提供电影、电视剧、综艺、动漫等高清免费在线观看服务。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://mogutv.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '蘑菇影视首页',
        item: 'https://mogutv.com/',
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '蘑菇影视官网 | 电影电视剧免费在线观看全集',
    description: '蘑菇影视官网首页入口，为您提供电影、电视剧、综艺、动漫等高清免费在线观看服务，支持手机电脑版访问，播放流畅不缓冲。',
    url: 'https://mogutv.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '蘑菇影视',
      url: 'https://mogutv.com',
    },
  };

  return (
    <>
      <SEOHead
        title="蘑菇影视官网 | 电影电视剧免费在线观看全集"
        description="蘑菇影视官网首页入口，为您提供电影、电视剧、综艺、动漫等高清免费在线观看服务，支持手机电脑版访问，播放流畅不缓冲。海量影视资源每日更新，热门大片抢先观看。"
        canonicalUrl="https://mogutv.com/"
        structuredData={{
          '@graph': [organizationSchema, websiteSchema, breadcrumbSchema, webPageSchema],
        }}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[420px] md:h-[600px] overflow-hidden">
          {heroSlides.map((slide, index) => (
            <Link
              key={slide.id}
              to={slide.link}
              className={`absolute inset-0 transition-opacity duration-700 ${
                currentSlide === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <img
                src={slide.imageUrl}
                alt={slide.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <span className="inline-block px-4 py-1.5 bg-accent-500/90 text-background-50 text-xs font-medium rounded-full mb-4 whitespace-nowrap">
                  {slide.tag}
                </span>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                  {slide.title}
                </h2>
                <p className="text-sm md:text-base text-background-200 max-w-lg">
                  {slide.subtitle}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-fill text-lg"></i>
                  </div>
                  {t('home.heroCta')}
                </span>
              </div>
            </Link>
          ))}

          {/* Slide indicators */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  currentSlide === index
                    ? 'w-6 h-2 bg-primary-500'
                    : 'w-2 h-2 bg-background-50/50 hover:bg-background-50/80'
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Arrow buttons */}
          <button
            onClick={() =>
              goToSlide(
                (currentSlide - 1 + heroSlides.length) % heroSlides.length,
              )
            }
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 transition-all duration-200 z-10 cursor-pointer"
            aria-label="Previous slide"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-xl"></i>
            </div>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 transition-all duration-200 z-10 cursor-pointer"
            aria-label="Next slide"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xl"></i>
            </div>
          </button>
        </section>

        {/* Category Navigation */}
        <section className="py-8 md:py-10 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-center gap-3 md:gap-4 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-6 py-3 bg-background-50 border border-background-200/70 rounded-full text-sm font-medium text-foreground-700 hover:text-primary-500 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-base`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {t('home.hotRecommend')}
                </h2>
              </div>
              <Link
                to="/category/dianying"
                className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                {t('home.viewAll')}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* New Arrivals */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {t('home.newArrival')}
                </h2>
              </div>
              <Link
                to="/category/dianying"
                className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
              >
                {t('home.viewAll')}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {newMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* All Movies Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <div className="w-1 h-6 bg-secondary-500 rounded-full"></div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                {t('home.allMovies')}
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {allMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}